import { noddiAsync, URLKeys } from 'noddi-async';
import { customerRoutes } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useLocation } from 'react-router-dom';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { useIsPersonalInfoPromptHandled } from '../../stores/confirmPersonalInfoStore';
import { useShowUserGroupDialog } from '../../stores/userGroupDialogStore';
import { ConfirmPersonalInfoFlow } from './ConfirmPersonalInfoFlow';

/**
 * Asks a signed-in private customer to review the personal information we have on file. Brands opt
 * in to the review and pick which parts it covers — contact details, cars, addresses — and for those
 * that have, the backend decides when it is due using the brand's configured review interval.
 */
export const ConfirmPersonalInfoDialog = () => {
  const { isLoggedIn, userData, impersonatedUser, currentUserGroupId } = useAuthContext();
  const isPromptHandled = useIsPersonalInfoPromptHandled(userData?.user.id);
  const showUserGroupDialog = useShowUserGroupDialog();
  const { pathname } = useLocation();
  const { currentBrand } = useCurrentBrand();

  // Per-brand opt-in. The app root already loads the brand for the availability check, so this
  // is served from the cache; a brand that has not opted in never even asks the backend for status.
  // A failed brand lookup counts as not opted in, and so does a brand with every section deselected.
  const { data: brand, error: brandError } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: { brandDomain: currentBrand }
  });
  const sections = brand?.personalInfoReviewSections ?? [];

  // Where signing in lands you, and nowhere else. An allowlist rather than a booking-flow blocklist:
  // a prompt across a booking, a payment or a proposal costs a conversion, and a new route added
  // later should be excluded until someone decides otherwise. The account-selection page is left
  // out on purpose: the personal account is preselected there, so the prompt would jump on someone
  // who is about to pick their business account.
  const isOnPostLoginSurface = [customerRoutes.homepage.getPath(), customerRoutes.home.getPath()].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

  // Private customers only. The review reads and writes the personal user group — its cars and
  // addresses — and a business account has far too many cars to review in a dialog; that needs a
  // fleet page of its own. So the prompt waits until the personal group is the selected one.
  const selectedUserGroup = userData?.user.userGroups.find((userGroup) => userGroup.id === currentUserGroupId);
  const personalUserGroupId = selectedUserGroup?.isPersonal ? selectedUserGroup.id : undefined;

  // Staff impersonating a customer must not confirm personal info on their behalf, and the
  // user group dialog is blocking — this prompt waits its turn.
  const canPrompt =
    isLoggedIn &&
    isOnPostLoginSurface &&
    !impersonatedUser &&
    !isPromptHandled &&
    !showUserGroupDialog &&
    personalUserGroupId !== undefined &&
    !brandError &&
    brand?.promptPersonalInfoUpdate === true &&
    sections.length > 0;

  // Answered once per session and never refetched: the flow saves the contact details before it asks
  // about cars and addresses, so a re-read here would come back "already confirmed" and close the
  // dialog out from under the user mid-flow. Dismissal is owned by the store, not by this query.
  const { data: personalInfoStatus, error: personalInfoStatusError } = noddiAsync.useGet({
    type: URLKeys.getPersonalInfoStatus,
    input: { brandDomain: currentBrand },
    queryConfig: {
      enabled: canPrompt,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  });

  // A background prompt must never break the app it sits on top of, so a failed lookup just skips it.
  if (
    !canPrompt ||
    personalInfoStatusError ||
    !personalInfoStatus?.promptUpdatePersonalInformation ||
    !userData ||
    personalUserGroupId === undefined
  ) {
    return null;
  }

  return (
    <ConfirmPersonalInfoFlow userId={userData.user.id} personalUserGroupId={personalUserGroupId} sections={sections} />
  );
};
